import { ESnapshotExists, ENotice, EFirebaseContext, exists } from '../types';
import { NoticeType, NoticeStatusType } from '../enums';
import { UserNoticeModel } from '../model/objects/userNoticeModel';
import { getOrThrow } from '../utils/refs';

/**
 * Check if a notice is a display ad.
 */
export const isDisplay = (notice: ESnapshotExists<ENotice>): boolean => {
  return notice.data().noticeType === NoticeType.display_ad.value;
};

export interface DisplaySitePrecondition {
  id: string;
  title: string;
  description: string;
  status: 'pass' | 'fail' | 'warning';
  isBlocking: boolean;
}

export interface DisplaySiteEligibility {
  canSync: boolean;
  shouldDelete: boolean; // For cancelled notices that need cleanup
  preconditions: DisplaySitePrecondition[];
}

/**
 * Checks all preconditions for syncing a notice to the display site.
 */
export const checkDisplaySiteEligibility = async (
  notice: UserNoticeModel,
  ctx: EFirebaseContext
): Promise<DisplaySiteEligibility> => {
  const preconditions: DisplaySitePrecondition[] = [];
  let canSync = true;

  // Check if notice is a test notice
  const isTestNotice = notice.modelData.testNotice;
  preconditions.push({
    id: 'test-notice',
    title: 'Test Notice Status',
    description: isTestNotice
      ? 'This is a test notice submitted while the publisher was in implementation. Test notices are not displayed on public notice display sites.'
      : 'Notice is not a test notice.',
    status: isTestNotice ? 'fail' : 'pass',
    isBlocking: true
  });
  if (isTestNotice) {
    canSync = false;
  }

  // Check if public notice record is locked
  try {
    const firstPublicNoticeRef = ctx.publicNoticesRef().doc(`${notice.id}-0`);
    const firstPublicNoticeSnap = await firstPublicNoticeRef.get();
    const isLocked =
      exists(firstPublicNoticeSnap) &&
      (firstPublicNoticeSnap.data() as any).recordLocked;

    preconditions.push({
      id: 'record-locked',
      title: 'Display Site Record Lock',
      description: isLocked
        ? 'Public notice record is purposefully locked by manual action and cannot be updated.'
        : 'Display site record is not locked.',
      status: isLocked ? 'fail' : 'pass',
      isBlocking: true
    });
    if (isLocked) {
      canSync = false;
    }
  } catch (error) {
    // If we can't check the lock status, assume it's not locked
    preconditions.push({
      id: 'record-locked',
      title: 'Display Site Record Lock',
      description: 'Could not verify lock status.',
      status: 'warning',
      isBlocking: false
    });
  }

  // Check notice status
  const isCancelled =
    notice.modelData.noticeStatus === NoticeStatusType.cancelled.value;
  preconditions.push({
    id: 'notice-status',
    title: 'Notice Status',
    description: isCancelled
      ? 'Notice is cancelled and will be removed from display sites.'
      : 'Notice status allows display site syncing.',
    status: isCancelled ? 'fail' : 'pass',
    isBlocking: true
  });
  if (isCancelled) {
    canSync = false;
  }

  // Check confirmation requirement
  try {
    const newspaper = await getOrThrow(notice.modelData.newspaper);
    const requiresConfirmation = newspaper.data().requireConfirmation;
    const { isConfirmed } = notice;

    if (requiresConfirmation) {
      preconditions.push({
        id: 'confirmation',
        title: 'Notice Confirmation',
        description: isConfirmed
          ? 'Notice is confirmed and ready for publication.'
          : 'Notice requires confirmation before it can be displayed on public sites.',
        status: isConfirmed ? 'pass' : 'fail',
        isBlocking: true
      });
      if (!isConfirmed) {
        canSync = false;
      }
    } else {
      preconditions.push({
        id: 'confirmation',
        title: 'Notice Confirmation',
        description: 'Publisher does not require confirmation for notices.',
        status: 'pass',
        isBlocking: false
      });
    }
  } catch (error) {
    preconditions.push({
      id: 'confirmation',
      title: 'Notice Confirmation',
      description: 'Could not verify confirmation requirements.',
      status: 'warning',
      isBlocking: false
    });
  }

  return {
    canSync,
    shouldDelete: isCancelled,
    preconditions
  };
};
